import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { media } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, PageHeader, SectionHeader } from "@/components/ui-primitives";

const About = () => {
  const { t, lang } = useLanguage();
  return (
    <>
      <SEO
        path="/about"
        title={lang === "ru" ? "О Rota Yachting" : "About Rota Yachting"}
        description={
          lang === "ru"
            ? "Rota Yachting — яхтенное подразделение Rota Denizcilik, турецкой морской группы, основанной в 1990 году в Синопе."
            : "Rota Yachting is the yacht division of Rota Denizcilik, a Turkish maritime group founded in 1990 in Sinop."
        }
      />
      <PageHeader
        eyebrow={t.about.eyebrow}
        title={t.about.title}
        intro={t.about.intro}
        image="/assets/photos/img_0660.jpg"
        testId="about-header"
      />

      <section className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16" data-testid="about-sections">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — editorial text column */}
          <div className="lg:col-span-7 space-y-12 lg:space-y-16">
            {t.about.sections.map((s, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className={`pb-10 ${i < t.about.sections.length - 1 ? "border-b border-navy/10" : ""}`}>
                  <div className="overline">0{i + 1}</div>
                  <h2 className="font-display text-3xl sm:text-4xl text-navy mt-3 leading-tight">
                    {s.h}
                  </h2>
                  <p className="mt-5 text-navy/70 text-base lg:text-lg leading-relaxed">{s.p}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          {/* Right — sticky chart-frame image (slides in from right on view) */}
          <aside className="lg:col-span-5 hidden lg:block">
            <motion.div
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="sticky top-28"
              data-testid="about-sticky-image"
            >
              <div className="relative aspect-[4/5] overflow-hidden chart-frame">
                <img
                  src="/assets/photos/about-sticky.jpg"
                  alt={t.about.title}
                  className="w-full h-full object-cover kenburns"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent pointer-events-none" />

                {/* Editorial caption — no LIVE badge */}
                <div className="absolute bottom-6 left-8 right-8">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 mb-1">
                    Since 1990
                  </div>
                  <div className="font-display text-2xl text-white leading-tight">
                    Sinop · Türkiye
                  </div>
                </div>
              </div>

              {/* Caption strip under the image */}
              <div className="mt-5 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-navy/50">
                <span className="w-6 h-px bg-brand/60" />
                <span>{lang === "ru" ? "Синоп · Родной порт" : lang === "tr" ? "Sinop · Ana liman" : "Sinop · Home port"}</span>
              </div>
            </motion.div>
          </aside>
        </div>
      </section>

      <section className="py-24 bg-cream border-y border-navy/10 px-6 sm:px-10 lg:px-16">
        <SectionHeader
          eyebrow="Values"
          title={t.about.valuesTitle}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
          {t.about.values.map((v, i) => (
            <FadeUp key={i} delay={i * 0.05}>
              <div className="bg-cream p-8 h-full">
                <div className="font-display text-5xl text-brand/30 mb-4">
                  0{i + 1}
                </div>
                <h3 className="font-display text-2xl text-navy mb-3">{v.h}</h3>
                <p className="text-sm text-navy/70 leading-relaxed">{v.p}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Heritage Timeline — 1990 Sinop → today */}
      <section className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16" data-testid="about-timeline">
        <SectionHeader
          eyebrow={lang === "ru" ? "Наследие" : lang === "tr" ? "Miras" : "Heritage"}
          title={
            lang === "ru"
              ? "От Синопа 1990 года — к яхтенной агентурной сети сегодня"
              : lang === "tr"
              ? "1990 Sinop'tan bugünün yat acentelik ağına"
              : "From 1990 in Sinop to today's yachting agency network"
          }
        />
        <div className="mt-16 max-w-4xl mx-auto relative">
          {/* Vertical timeline line */}
          <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-navy/15 -translate-x-px" />
          {[
            {
              year: "1990",
              en: { h: "Founded in Sinop", p: "Rota Denizcilik launched by the Levend family at Sinop's natural harbour — the largest naturally protected port on Türkiye's Black Sea coast." },
              ru: { h: "Основание в Синопе", p: "Rota Denizcilik основана семьёй Левенд в природной гавани Синопа — крупнейшем естественно защищённом порту турецкого Чёрного моря." },
              tr: { h: "Sinop'ta kuruldu", p: "Rota Denizcilik, Türkiye'nin Karadeniz kıyısındaki en büyük doğal korunaklı liman olan Sinop'un doğal limanında Levend ailesi tarafından kuruldu." },
            },
            {
              year: "1995",
              en: { h: "Black Sea network", p: "Branch operations expanded along the Turkish Black Sea — Trabzon, Samsun, Amasra, Karadeniz Ereğli — handling commercial and passenger ship agency." },
              ru: { h: "Черноморская сеть", p: "Расширение филиалов вдоль черноморского побережья Турции — Трабзон, Самсун, Амасра, Карадениз-Эрегли — агентское обслуживание коммерческих и пассажирских судов." },
              tr: { h: "Karadeniz ağı", p: "Türkiye Karadeniz kıyısı boyunca şube operasyonları genişletildi — Trabzon, Samsun, Amasra, Karadeniz Ereğli — ticari ve yolcu gemi acentelik hizmetleri." },
            },
            {
              year: "2005",
              en: { h: "İstanbul & Marmara", p: "Operations extended to İstanbul, the Bosphorus and Çanakkale strait — adding superyacht-relevant transit, customs and Marmara port services." },
              ru: { h: "Стамбул и Мармара", p: "Расширение в Стамбул, Босфор и пролив Чанаккале — добавлены релевантные для суперъяхт транзитные, таможенные и портовые услуги Мармары." },
              tr: { h: "İstanbul ve Marmara", p: "Operasyonlar İstanbul, Boğaziçi ve Çanakkale Boğazı'na uzatıldı — süperyatlara uygun transit, gümrük ve Marmara liman hizmetleri eklendi." },
            },
            {
              year: "2015",
              en: { h: "450,000+ operations", p: "Group milestone: more than 450,000 maritime operations completed across 30+ Turkish ports for shipping companies, cruise operators and private vessels." },
              ru: { h: "Более 450 000 операций", p: "Веха группы: более 450 000 морских операций выполнены в более чем 30 турецких портах для судоходных компаний, круизных операторов и частных судов." },
              tr: { h: "450.000+ operasyon", p: "Grup kilometre taşı: 30+ Türk limanında armatörlük şirketleri, kruvaziyer operatörleri ve özel gemiler için 450.000'den fazla denizcilik operasyonu tamamlandı." },
            },
            {
              year: "2016",
              en: { h: "Rota Yachting launched", p: "Yacht-only division established to apply 35 years of Turkish maritime authority exclusively to private and charter yachts — full-service agency, no brokerage." },
              ru: { h: "Запуск Rota Yachting", p: "Создано подразделение, занимающееся исключительно яхтами — 35 лет турецкого морского авторитета применяются только к частным и чартерным яхтам. Полный спектр агентских услуг, без брокерства." },
              tr: { h: "Rota Yachting kuruldu", p: "35 yıllık Türk denizcilik otoritesini yalnızca özel ve charter yatlara uygulamak üzere yat odaklı birim kuruldu — tam kapsamlı acente, brokerlik yok." },
            },
            {
              year: "2026",
              en: { h: "30-location agency network", p: "Live agency coverage across the Black Sea, Marmara, Aegean and Mediterranean — single point of contact for any yacht operation in Turkish waters." },
              ru: { h: "Агентская сеть на 30 локациях", p: "Активное агентское присутствие на Чёрном, Мраморном, Эгейском и Средиземном морях — единая точка контакта для любых яхтенных операций в турецких водах." },
              tr: { h: "30 lokasyonlu acentelik ağı", p: "Karadeniz, Marmara, Ege ve Akdeniz'de aktif acentelik kapsamı — Türk sularındaki her yat operasyonu için tek temas noktası." },
            },
          ].map((m, i) => {
            const copy = m[lang] || m.en;
            const isLeft = i % 2 === 0;
            return (
              <FadeUp key={m.year} delay={i * 0.05}>
                <div className={`relative pb-12 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-12 ${isLeft ? "" : "sm:flex-row-reverse"}`}>
                  {/* Year dot */}
                  <div className="absolute left-4 sm:left-1/2 top-2 w-3 h-3 rounded-full bg-brand -translate-x-1/2 ring-4 ring-bone z-10" />
                  {isLeft ? (
                    <>
                      <div className="pl-12 sm:pl-0 sm:pr-8 sm:text-right">
                        <div className="font-display text-5xl sm:text-6xl text-brand/30 leading-none">{m.year}</div>
                        <h3 className="mt-3 font-display text-2xl text-navy">{copy.h}</h3>
                      </div>
                      <div className="pl-12 sm:pl-8">
                        <p className="text-navy/70 text-sm leading-relaxed">{copy.p}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="pl-12 sm:pl-0 sm:order-2 sm:pl-8">
                        <div className="font-display text-5xl sm:text-6xl text-brand/30 leading-none">{m.year}</div>
                        <h3 className="mt-3 font-display text-2xl text-navy">{copy.h}</h3>
                      </div>
                      <div className="pl-12 sm:pl-0 sm:order-1 sm:pr-8 sm:text-right">
                        <p className="text-navy/70 text-sm leading-relaxed">{copy.p}</p>
                      </div>
                    </>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <section className="py-24 px-6 sm:px-10 lg:px-16 text-center">
        <FadeUp>
          <h2 className="font-display text-4xl sm:text-5xl text-navy">
            {t.home.quickContactTitle}
          </h2>
          <p className="mt-5 text-navy/70 max-w-xl mx-auto">{t.home.quickContactBody}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 mt-10 bg-brand text-white px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors"
            data-testid="about-cta"
          >
            {t.nav.requestSupport} <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </section>
    </>
  );
};

export default About;
